/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  addDecoderSizePrefix,
  addEncoderSizePrefix,
  combineCodec,
  fixDecoderSize,
  fixEncoderSize,
  getAddressEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getProgramDerivedAddress,
  getStructDecoder,
  getStructEncoder,
  getU32Decoder,
  getU32Encoder,
  getU8Decoder,
  getU8Encoder,
  getUtf8Decoder,
  getUtf8Encoder,
  transformEncoder,
  type AccountMeta,
  type AccountSignerMeta,
  type Address,
  type Codec,
  type Decoder,
  type Encoder,
  type Instruction,
  type InstructionWithAccounts,
  type InstructionWithData,
  type ReadonlyAccount,
  type ReadonlyUint8Array,
  type TransactionSigner,
  type WritableAccount,
  type WritableSignerAccount,
} from "@solana/kit";
import { GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS } from "../programs";
import {
  expectAddress,
  expectSome,
  getAccountMetaFactory,
  type ResolvedAccount,
} from "../shared";
import {
  getPricingModelDecoder,
  getPricingModelEncoder,
  type PricingModel,
  type PricingModelArgs,
} from "../types";

export const REGISTER_AGENT_DISCRIMINATOR = new Uint8Array([
  135, 157, 66, 195, 2, 113, 175, 30,
]);

export function getRegisterAgentDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(
    REGISTER_AGENT_DISCRIMINATOR,
  );
}

export type RegisterAgentInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountAgentAccount extends string | AccountMeta<string> = string,
  TAccountStakingAccount extends string | AccountMeta<string> = string,
  TAccountSigner extends string | AccountMeta<string> = string,
  TAccountSystemProgram extends string | AccountMeta<string> =
    "11111111111111111111111111111111",
  TAccountClock extends string | AccountMeta<string> =
    "SysvarC1ock11111111111111111111111111111111",
  TRemainingAccounts extends readonly AccountMeta<string>[] = [],
> = Instruction<TProgram> &
  InstructionWithData<ReadonlyUint8Array> &
  InstructionWithAccounts<
    [
      TAccountAgentAccount extends string
        ? WritableAccount<TAccountAgentAccount>
        : TAccountAgentAccount,
      TAccountStakingAccount extends string
        ? ReadonlyAccount<TAccountStakingAccount>
        : TAccountStakingAccount,
      TAccountSigner extends string
        ? WritableSignerAccount<TAccountSigner> &
            AccountSignerMeta<TAccountSigner>
        : TAccountSigner,
      TAccountSystemProgram extends string
        ? ReadonlyAccount<TAccountSystemProgram>
        : TAccountSystemProgram,
      TAccountClock extends string
        ? ReadonlyAccount<TAccountClock>
        : TAccountClock,
      ...TRemainingAccounts,
    ]
  >;

export type RegisterAgentInstructionData = {
  discriminator: ReadonlyUint8Array;
  agentType: number;
  name: string;
  description: string;
  metadataUri: string;
  agentId: string;
  pricingModel: PricingModel;
};

export type RegisterAgentInstructionDataArgs = {
  agentType: number;
  name: string;
  description: string;
  metadataUri: string;
  agentId: string;
  pricingModel: PricingModelArgs;
};

export function getRegisterAgentInstructionDataEncoder(): Encoder<RegisterAgentInstructionDataArgs> {
  return transformEncoder(
    getStructEncoder([
      ["discriminator", fixEncoderSize(getBytesEncoder(), 8)],
      ["agentType", getU8Encoder()],
      ["name", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
      ["description", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
      ["metadataUri", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
      ["agentId", addEncoderSizePrefix(getUtf8Encoder(), getU32Encoder())],
      ["pricingModel", getPricingModelEncoder()],
    ]),
    (value) => ({ ...value, discriminator: REGISTER_AGENT_DISCRIMINATOR }),
  );
}

export function getRegisterAgentInstructionDataDecoder(): Decoder<RegisterAgentInstructionData> {
  return getStructDecoder([
    ["discriminator", fixDecoderSize(getBytesDecoder(), 8)],
    ["agentType", getU8Decoder()],
    ["name", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    ["description", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    ["metadataUri", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    ["agentId", addDecoderSizePrefix(getUtf8Decoder(), getU32Decoder())],
    ["pricingModel", getPricingModelDecoder()],
  ]);
}

export function getRegisterAgentInstructionDataCodec(): Codec<
  RegisterAgentInstructionDataArgs,
  RegisterAgentInstructionData
> {
  return combineCodec(
    getRegisterAgentInstructionDataEncoder(),
    getRegisterAgentInstructionDataDecoder(),
  );
}

export type RegisterAgentAsyncInput<
  TAccountAgentAccount extends string = string,
  TAccountStakingAccount extends string = string,
  TAccountSigner extends string = string,
  TAccountSystemProgram extends string = string,
  TAccountClock extends string = string,
> = {
  /** Agent account with enhanced 2025 PDA security */
  agentAccount?: Address<TAccountAgentAccount>;
  /** Staking account - REQUIRED for Sybil resistance (must have >= 1K GHOST staked) */
  stakingAccount?: Address<TAccountStakingAccount>;
  /**
   * User registry with enhanced validation
   * Authority with enhanced verification
   */
  signer: TransactionSigner<TAccountSigner>;
  /** System program for account creation */
  systemProgram?: Address<TAccountSystemProgram>;
  /** Clock sysvar for timestamp validation */
  clock?: Address<TAccountClock>;
  agentType: RegisterAgentInstructionDataArgs["agentType"];
  name: RegisterAgentInstructionDataArgs["name"];
  description: RegisterAgentInstructionDataArgs["description"];
  metadataUri: RegisterAgentInstructionDataArgs["metadataUri"];
  agentId: RegisterAgentInstructionDataArgs["agentId"];
  pricingModel: RegisterAgentInstructionDataArgs["pricingModel"];
};

export async function getRegisterAgentInstructionAsync<
  TAccountAgentAccount extends string,
  TAccountStakingAccount extends string,
  TAccountSigner extends string,
  TAccountSystemProgram extends string,
  TAccountClock extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: RegisterAgentAsyncInput<
    TAccountAgentAccount,
    TAccountStakingAccount,
    TAccountSigner,
    TAccountSystemProgram,
    TAccountClock
  >,
  config?: { programAddress?: TProgramAddress },
): Promise<
  RegisterAgentInstruction<
    TProgramAddress,
    TAccountAgentAccount,
    TAccountStakingAccount,
    TAccountSigner,
    TAccountSystemProgram,
    TAccountClock
  >
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    agentAccount: { value: input.agentAccount ?? null, isWritable: true },
    stakingAccount: { value: input.stakingAccount ?? null, isWritable: false },
    signer: { value: input.signer ?? null, isWritable: true },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
    clock: { value: input.clock ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.agentAccount.value) {
    accounts.agentAccount.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(new Uint8Array([97, 103, 101, 110, 116])),
        getAddressEncoder().encode(expectAddress(accounts.signer.value)),
        getUtf8Encoder().encode(expectSome(args.agentId)),
      ],
    });
  }
  if (!accounts.stakingAccount.value) {
    accounts.stakingAccount.value = await getProgramDerivedAddress({
      programAddress,
      seeds: [
        getBytesEncoder().encode(
          new Uint8Array([115, 116, 97, 107, 105, 110, 103]),
        ),
        getAddressEncoder().encode(expectAddress(accounts.signer.value)),
      ],
    });
  }
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      "11111111111111111111111111111111" as Address<"11111111111111111111111111111111">;
  }
  if (!accounts.clock.value) {
    accounts.clock.value =
      "SysvarC1ock11111111111111111111111111111111" as Address<"SysvarC1ock11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.agentAccount),
      getAccountMeta(accounts.stakingAccount),
      getAccountMeta(accounts.signer),
      getAccountMeta(accounts.systemProgram),
      getAccountMeta(accounts.clock),
    ],
    data: getRegisterAgentInstructionDataEncoder().encode(
      args as RegisterAgentInstructionDataArgs,
    ),
    programAddress,
  } as RegisterAgentInstruction<
    TProgramAddress,
    TAccountAgentAccount,
    TAccountStakingAccount,
    TAccountSigner,
    TAccountSystemProgram,
    TAccountClock
  >);
}

export type RegisterAgentInput<
  TAccountAgentAccount extends string = string,
  TAccountStakingAccount extends string = string,
  TAccountSigner extends string = string,
  TAccountSystemProgram extends string = string,
  TAccountClock extends string = string,
> = {
  /** Agent account with enhanced 2025 PDA security */
  agentAccount: Address<TAccountAgentAccount>;
  /** Staking account - REQUIRED for Sybil resistance (must have >= 1K GHOST staked) */
  stakingAccount: Address<TAccountStakingAccount>;
  /**
   * User registry with enhanced validation
   * Authority with enhanced verification
   */
  signer: TransactionSigner<TAccountSigner>;
  /** System program for account creation */
  systemProgram?: Address<TAccountSystemProgram>;
  /** Clock sysvar for timestamp validation */
  clock?: Address<TAccountClock>;
  agentType: RegisterAgentInstructionDataArgs["agentType"];
  name: RegisterAgentInstructionDataArgs["name"];
  description: RegisterAgentInstructionDataArgs["description"];
  metadataUri: RegisterAgentInstructionDataArgs["metadataUri"];
  agentId: RegisterAgentInstructionDataArgs["agentId"];
  pricingModel: RegisterAgentInstructionDataArgs["pricingModel"];
};

export function getRegisterAgentInstruction<
  TAccountAgentAccount extends string,
  TAccountStakingAccount extends string,
  TAccountSigner extends string,
  TAccountSystemProgram extends string,
  TAccountClock extends string,
  TProgramAddress extends Address =
    typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
>(
  input: RegisterAgentInput<
    TAccountAgentAccount,
    TAccountStakingAccount,
    TAccountSigner,
    TAccountSystemProgram,
    TAccountClock
  >,
  config?: { programAddress?: TProgramAddress },
): RegisterAgentInstruction<
  TProgramAddress,
  TAccountAgentAccount,
  TAccountStakingAccount,
  TAccountSigner,
  TAccountSystemProgram,
  TAccountClock
> {
  // Program address.
  const programAddress =
    config?.programAddress ?? GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS;

  // Original accounts.
  const originalAccounts = {
    agentAccount: { value: input.agentAccount ?? null, isWritable: true },
    stakingAccount: { value: input.stakingAccount ?? null, isWritable: false },
    signer: { value: input.signer ?? null, isWritable: true },
    systemProgram: { value: input.systemProgram ?? null, isWritable: false },
    clock: { value: input.clock ?? null, isWritable: false },
  };
  const accounts = originalAccounts as Record<
    keyof typeof originalAccounts,
    ResolvedAccount
  >;

  // Original args.
  const args = { ...input };

  // Resolve default values.
  if (!accounts.systemProgram.value) {
    accounts.systemProgram.value =
      "11111111111111111111111111111111" as Address<"11111111111111111111111111111111">;
  }
  if (!accounts.clock.value) {
    accounts.clock.value =
      "SysvarC1ock11111111111111111111111111111111" as Address<"SysvarC1ock11111111111111111111111111111111">;
  }

  const getAccountMeta = getAccountMetaFactory(programAddress, "programId");
  return Object.freeze({
    accounts: [
      getAccountMeta(accounts.agentAccount),
      getAccountMeta(accounts.stakingAccount),
      getAccountMeta(accounts.signer),
      getAccountMeta(accounts.systemProgram),
      getAccountMeta(accounts.clock),
    ],
    data: getRegisterAgentInstructionDataEncoder().encode(
      args as RegisterAgentInstructionDataArgs,
    ),
    programAddress,
  } as RegisterAgentInstruction<
    TProgramAddress,
    TAccountAgentAccount,
    TAccountStakingAccount,
    TAccountSigner,
    TAccountSystemProgram,
    TAccountClock
  >);
}

export type ParsedRegisterAgentInstruction<
  TProgram extends string = typeof GHOSTSPEAK_MARKETPLACE_PROGRAM_ADDRESS,
  TAccountMetas extends readonly AccountMeta[] = readonly AccountMeta[],
> = {
  programAddress: Address<TProgram>;
  accounts: {
    /** Agent account with enhanced 2025 PDA security */
    agentAccount: TAccountMetas[0];
    /** Staking account - REQUIRED for Sybil resistance (must have >= 1K GHOST staked) */
    stakingAccount: TAccountMetas[1];
    /**
     * User registry with enhanced validation
     * Authority with enhanced verification
     */
    signer: TAccountMetas[2];
    /** System program for account creation */
    systemProgram: TAccountMetas[3];
    /** Clock sysvar for timestamp validation */
    clock: TAccountMetas[4];
  };
  data: RegisterAgentInstructionData;
};

export function parseRegisterAgentInstruction<
  TProgram extends string,
  TAccountMetas extends readonly AccountMeta[],
>(
  instruction: Instruction<TProgram> &
    InstructionWithAccounts<TAccountMetas> &
    InstructionWithData<ReadonlyUint8Array>,
): ParsedRegisterAgentInstruction<TProgram, TAccountMetas> {
  if (instruction.accounts.length < 5) {
    // TODO: Coded error.
    throw new Error("Not enough accounts");
  }
  let accountIndex = 0;
  const getNextAccount = () => {
    const accountMeta = (instruction.accounts as TAccountMetas)[accountIndex]!;
    accountIndex += 1;
    return accountMeta;
  };
  return {
    programAddress: instruction.programAddress,
    accounts: {
      agentAccount: getNextAccount(),
      stakingAccount: getNextAccount(),
      signer: getNextAccount(),
      systemProgram: getNextAccount(),
      clock: getNextAccount(),
    },
    data: getRegisterAgentInstructionDataDecoder().decode(instruction.data),
  };
}
